import { useState } from 'react';
import {
  DndContext,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import type { TrainingOption } from '@hcc/types';

/**
 * Matching answer: drag a label chip onto its target slot (dnd-kit), or
 * tap a chip to arm it then tap a slot to place it — the tap path doubles
 * as the keyboard route since chips and slots are buttons. Value maps
 * target id -> placed label id. Label ids are server-derived and never
 * equal the pair ids, so the mapping itself reveals nothing.
 */
export function MatchAnswer({
  targets,
  labels,
  value,
  onChange,
  disabled,
  ariaLabelledBy,
}: {
  targets: TrainingOption[];
  labels: TrainingOption[];
  value: Record<string, string> | undefined;
  onChange: (next: Record<string, string>) => void;
  disabled?: boolean;
  ariaLabelledBy?: string;
}) {
  const placed = value ?? {};
  const [armed, setArmed] = useState<string | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const labelById = new Map(labels.map((l) => [l.id, l.label]));
  const usedLabelIds = new Set(Object.values(placed));
  const bank = labels.filter((l) => !usedLabelIds.has(l.id));

  function placeLabel(targetId: string, labelId: string) {
    // Placing a label that already sits in another slot moves it.
    const next: Record<string, string> = {};
    for (const [t, l] of Object.entries(placed)) {
      if (l !== labelId && t !== targetId) next[t] = l;
    }
    next[targetId] = labelId;
    onChange(next);
    setArmed(null);
  }

  function clearTarget(targetId: string) {
    const next = { ...placed };
    delete next[targetId];
    onChange(next);
  }

  function onDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (over) placeLabel(String(over.id), String(active.id));
  }

  const chipClass = (active: boolean) =>
    `min-h-[40px] rounded-token-sm border px-3 py-2 text-sm transition-colors disabled:opacity-60 ${
      active
        ? 'border-accent bg-accent text-fg-on-accent'
        : 'border-accent/40 bg-accent/10 text-fg hover:bg-accent/20'
    }`;

  return (
    <DndContext sensors={sensors} onDragEnd={onDragEnd}>
      <div role="group" aria-labelledby={ariaLabelledBy} className="flex flex-col gap-2">
        {targets.map((t) => (
          <TargetSlot
            key={t.id}
            target={t}
            placedLabel={placed[t.id] ? (labelById.get(placed[t.id]!) ?? null) : null}
            disabled={disabled}
            onTap={() => {
              if (disabled) return;
              if (armed) placeLabel(t.id, armed);
              else if (placed[t.id]) clearTarget(t.id);
            }}
          />
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-2" aria-label="Answers to place">
        {bank.map((l) => (
          <DraggableChip
            key={l.id}
            id={l.id}
            label={l.label}
            disabled={disabled}
            className={chipClass(armed === l.id)}
            pressed={armed === l.id}
            onTap={() => setArmed((cur) => (cur === l.id ? null : l.id))}
          />
        ))}
        {bank.length === 0 ? (
          <p className="text-xs text-fg-muted">All placed — tap a slot to take its answer back.</p>
        ) : null}
      </div>
      <p className="mt-1.5 text-xs text-fg-muted">
        Drag an answer onto its match, or tap the answer then tap where it belongs.
      </p>
    </DndContext>
  );
}

function TargetSlot({
  target,
  placedLabel,
  disabled,
  onTap,
}: {
  target: TrainingOption;
  placedLabel: string | null;
  disabled?: boolean;
  onTap: () => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: target.id, disabled });
  return (
    <div className="flex items-center gap-3">
      <span className="w-2/5 shrink-0 text-sm font-medium text-fg">{target.label}</span>
      <button
        ref={setNodeRef}
        type="button"
        disabled={disabled}
        onClick={onTap}
        aria-label={
          placedLabel
            ? `${target.label}: matched with ${placedLabel}. Tap to remove.`
            : `${target.label}: empty slot`
        }
        className={`min-h-[44px] flex-1 rounded-token-sm border-2 border-dashed px-3 py-2 text-left text-sm transition-colors ${
          isOver
            ? 'border-accent bg-accent/10'
            : placedLabel
              ? 'border-accent/40 bg-accent/10 text-fg'
              : 'border-line-strong/30 bg-surface-2 text-fg-dim'
        }`}
      >
        {placedLabel ?? 'Drop here'}
      </button>
    </div>
  );
}

function DraggableChip({
  id,
  label,
  disabled,
  className,
  pressed,
  onTap,
}: {
  id: string;
  label: string;
  disabled?: boolean;
  className: string;
  pressed: boolean;
  onTap: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id,
    disabled,
  });
  return (
    <button
      ref={setNodeRef}
      type="button"
      disabled={disabled}
      onClick={onTap}
      {...attributes}
      {...listeners}
      aria-pressed={pressed}
      style={
        transform
          ? { transform: `translate(${transform.x}px, ${transform.y}px)`, zIndex: 10 }
          : undefined
      }
      className={`touch-none ${isDragging ? 'opacity-80 shadow-token' : ''} ${className}`}
    >
      {label}
    </button>
  );
}
